import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export default function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm(""); setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New password and confirmation do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setOpen(false);
      reset();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="change-password-open"
          className="mt-3 h-8 w-full rounded-full border-gold-200 bg-white text-xs text-navy-700 hover:bg-gold-50"
        >
          <KeyRound className="mr-1.5 h-3.5 w-3.5 text-gold-600" /> Change password
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy-900">Change password</DialogTitle>
          <DialogDescription className="text-navy-500">Update the password for your admin account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Current password</Label>
            <Input type="password" data-testid="change-current-input" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1 focus-visible:ring-gold-500" required />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" data-testid="change-new-input" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 focus-visible:ring-gold-500" placeholder="At least 8 characters" required />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" data-testid="change-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 focus-visible:ring-gold-500" required />
          </div>
          {error && <div data-testid="change-password-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" data-testid="change-password-submit" disabled={loading} className="rounded-full bg-gold-500 text-white hover:bg-gold-600">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
